import React, { createContext, useCallback, useContext, useState } from 'react';
import Button from '../components/common/Button';

const ConfirmContext = createContext();

// Promise-based replacement for window.confirm(): `await confirm({ title, message })`
// resolves true/false. Keeps the same "ask, then act" flow call sites already
// use, just rendered as an on-brand modal instead of the native browser dialog.
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setState({
        title: 'Please confirm',
        message: '',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        variant: 'danger',
        ...options,
        resolve
      });
    });
  }, []);

  const respond = (result) => {
    state?.resolve(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
          onClick={() => respond(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 slide-in border border-transparent dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${state.variant === 'danger' ? 'bg-red-100 text-red-600 dark:bg-red-500/10 dark:text-red-400' : 'bg-primary/10 text-primary dark:bg-accent/10 dark:text-accent'}`}>
                <i className={`fas ${state.variant === 'danger' ? 'fa-triangle-exclamation' : 'fa-circle-question'}`}></i>
              </div>
              <div>
                <h3 id="confirm-dialog-title" className="text-lg font-bold text-slate-900 dark:text-slate-100">{state.title}</h3>
                {state.message && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{state.message}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={() => respond(false)}>{state.cancelLabel}</Button>
              <Button variant={state.variant === 'danger' ? 'danger' : 'primary'} onClick={() => respond(true)}>
                {state.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};
